/**
 * Turning a stream of wheel events into hotbar steps.
 *
 * A notched mouse wheel sends one event per notch, so "one event, one slot"
 * worked and looked like it was the whole problem. A Magic Mouse - or any
 * trackpad - sends a continuous stream of small deltas while the finger
 * moves, and then keeps sending a decaying tail of them for a second or more
 * after it has lifted. On that hardware one flick was dozens of events and
 * the selection span across the hotbar and back.
 *
 * Three rules fix it, and all three are needed:
 *
 *  - **Distance, not events.** Deltas are accumulated and one slot is worth a
 *    fixed amount of scrolling. A notch of a real wheel is about that amount,
 *    so wheels behave exactly as before.
 *  - **A floor on how fast slots can pass.** However hard the stream comes in,
 *    the selection moves at a pace a person can watch and stop.
 *  - **A new gesture starts from nothing.** After a pause the running total is
 *    dropped, so a flick never inherits the leftovers of the last one.
 *
 * Pure arithmetic over (delta, mode, timestamp). No DOM: the browser's
 * WheelEvent is three numbers as far as this is concerned, which is what lets
 * a Magic Mouse burst be replayed in a test instead of felt by hand.
 */

/**
 * How much scrolling moves the selection one slot, in pixels.
 *
 * A notch of a conventional wheel reports about a hundred, so a wheel still
 * steps once per notch.
 */
export const PIXELS_PER_SLOT = 100;

/** The least time between two slots passing, in milliseconds. */
export const MIN_STEP_MS = 130;

/** Quiet for this long and the next event begins a fresh gesture. */
export const GESTURE_GAP_MS = 250;

/**
 * What one line and one page are worth in pixels.
 *
 * Browsers disagree about which unit they report in: Chrome usually sends
 * pixels, Firefox lines. Without this a wheel that reports "3 lines" would
 * count as three pixels and never move anything at all.
 */
const PIXELS_PER_LINE = 40;
const PIXELS_PER_PAGE = 800;

export class WheelSteps {
  /** Scrolling seen so far this gesture, in pixels, signed. */
  private carry = 0;
  /** When a slot last passed, and when anything last arrived. */
  private lastStep = -Infinity;
  private lastEvent = -Infinity;

  /**
   * Feed one wheel event.
   *
   * @param delta the event's deltaY.
   * @param mode the event's deltaMode: 0 pixels, 1 lines, 2 pages.
   * @param now a millisecond clock.
   * @returns -1, 0 or +1: how far to move the selection. Never more than one
   * slot at a time, because a burst that moves four at once is a burst nobody
   * can aim with.
   */
  step(delta: number, mode: number, now: number): number {
    if (now - this.lastEvent > GESTURE_GAP_MS) this.carry = 0;
    this.lastEvent = now;

    const pixels = delta * (
      mode === 1 ? PIXELS_PER_LINE : mode === 2 ? PIXELS_PER_PAGE : 1
    );
    // A change of direction abandons whatever was building up the other way.
    // Without this, flicking back after a flick forward spends the first
    // flick's leftovers before it starts on the second.
    if (Math.sign(pixels) !== Math.sign(this.carry)) this.carry = 0;
    this.carry += pixels;

    if (Math.abs(this.carry) < PIXELS_PER_SLOT) return 0;
    if (now - this.lastStep < MIN_STEP_MS) {
      // Hold the total at one slot's worth. Letting it pile up during the
      // cooldown would spend the whole inertial tail the moment the cooldown
      // ended, which is the behaviour this exists to stop.
      this.carry = Math.sign(this.carry) * PIXELS_PER_SLOT;
      return 0;
    }

    this.lastStep = now;
    const direction = Math.sign(this.carry);
    this.carry -= direction * PIXELS_PER_SLOT;
    return direction;
  }

  /** Forget the gesture: the panel opened, the pointer was released. */
  reset(): void {
    this.carry = 0;
    this.lastStep = -Infinity;
    this.lastEvent = -Infinity;
  }
}
